// UI 文言の多言語化（i18n）。文言は { ja, en } の辞書に集約し、t(key, params) で引く。
// 言語は localStorage('immerso-lang') の明示指定 → 端末ロケール の順で決定（既定: ja 以外は en）。
// 言語切替は setLang() が保存して location.reload()（全ビューを描き直す）。
// 方針: アイコン/装飾の絵文字・記号は使わない（CLAUDE.md）。文言中の {name} は params で差し込む。

const LANG_KEY = 'immerso-lang'
const SUPPORTED = ['ja', 'en']

function detectLang() {
  const saved = (() => {
    try {
      return localStorage.getItem(LANG_KEY)
    } catch {
      return null
    }
  })()
  if (saved && SUPPORTED.includes(saved)) return saved
  const nav = (navigator.language || '').toLowerCase()
  return nav.startsWith('ja') ? 'ja' : 'en'
}

let lang = detectLang()

export function getLang() {
  return lang
}

export function setLang(next) {
  if (!SUPPORTED.includes(next) || next === lang) return
  try {
    localStorage.setItem(LANG_KEY, next)
  } catch {
    /* 保存できなくても続行 */
  }
  location.reload() // 全ビュー・タブバーを新言語で描き直す
}

// 文言辞書。キーはドット区切りの名前空間。値は { ja, en }。{name} は params で置換。
const STR = {
  // --- ナビ（タブ） ---
  'nav.library': { ja: 'ライブラリ', en: 'Library' },
  'nav.cloud': { ja: 'クラウド', en: 'Cloud' },
  'nav.local': { ja: 'ローカル', en: 'Local' },
  'nav.library': { ja: 'ライブラリ', en: 'Library' },
  'nav.words': { ja: '単語', en: 'Words' },
  'nav.review': { ja: '復習', en: 'Review' },
  'nav.settings': { ja: '設定', en: 'Settings' },
  'local.title': { ja: 'ローカル', en: 'Local' },
  'local.empty': {
    ja: '端末に取り込んだ音声がここに表示されます。プランに関係なくいつでも使えます。右上の「＋追加」から取り込めます。',
    en: 'Audio you import on this device appears here, usable on any plan. Use "+ Add" at the top right.',
  },
  'local.add': { ja: '追加', en: 'Add' },
  'local.importing': { ja: '取り込み中… {name}（{i}/{n}）', en: 'Importing… {name} ({i}/{n})' },
  'local.segmenting': { ja: '区切りを解析中… {name}', en: 'Detecting segments… {name}' },
  'local.imported': { ja: '{n} 件を取り込みました', en: 'Imported {n} item(s)' },
  'local.importFailed': { ja: '取り込み失敗: {msg}', en: 'Import failed: {msg}' },
  'local.segments': { ja: '{n} 区切り', en: '{n} segments' },
  'local.subtitle': { ja: '字幕', en: 'Subtitle' },
  'local.more': { ja: 'メニュー', en: 'Menu' },
  'local.newFolder': { ja: 'フォルダ', en: 'Folder' },
  'local.newFolderTitle': { ja: 'フォルダを作成', en: 'Create folder' },
  'local.renameTitle': { ja: 'フォルダ名を変更', en: 'Rename folder' },
  'local.rename': { ja: '名前を変更', en: 'Rename' },
  'local.open': { ja: '開く', en: 'Open' },
  'local.move': { ja: 'フォルダへ移動', en: 'Move to folder' },
  'local.moveTitle': { ja: '移動先を選ぶ', en: 'Move to' },
  'local.moveRoot': { ja: 'ローカル（最上位）', en: 'Local (top level)' },
  'local.deleteFolder': { ja: 'フォルダを削除', en: 'Delete folder' },
  'local.deleteFolderConfirm': {
    ja: '「{name}」を削除しますか？中の項目とサブフォルダは最上位に戻ります（ファイルは消えません）。',
    en: 'Delete "{name}"? Its items and subfolders move to the top level (files are kept).',
  },
  'local.emptyFolder': { ja: 'このフォルダは空です。右上の「＋追加」やメニューの「フォルダへ移動」で入れられます。', en: 'This folder is empty. Add items via "+ Add" or "Move to folder".' },
  'local.menu.play': { ja: '再生', en: 'Play' },
  'local.menu.addSub': { ja: '字幕を取り込む（SRT / VTT）', en: 'Import subtitles (SRT / VTT)' },
  'local.menu.addSubHint': {
    ja: '何個でも取り込めます。メイン/サブのどちらに表示するかは、再生中の字幕設定で後から変えられます。',
    en: 'Import as many as you like. Choose which one shows as main/sub later in the player\'s subtitle settings.',
  },
  'local.menu.upload': { ja: 'Drive にアップロード', en: 'Upload to Drive' },
  'local.menu.uploadHint': {
    ja: '連携した Google Drive に上げて、他の端末でも使えるようにします。',
    en: 'Uploads to your Google Drive so other devices can use it.',
  },
  'local.uploadNeedConnect': { ja: '先に設定で Google Drive と連携してください。', en: 'Connect Google Drive in Settings first.' },
  'local.uploading': { ja: '「{name}」をアップロード中…', en: 'Uploading "{name}"…' },
  'local.uploadingPhase': { ja: '「{name}」をアップロード中…（{phase}）', en: 'Uploading "{name}"… ({phase})' },
  'local.uploadPhase.media': { ja: 'メディア', en: 'media' },
  'local.uploadPhase.bundle': { ja: '字幕・メタ', en: 'subtitles/meta' },
  'local.uploadPhase.catalog': { ja: 'カタログ', en: 'catalog' },
  'local.uploadPhase.done': { ja: '完了', en: 'done' },
  'local.uploaded': { ja: '「{name}」を Drive にアップロードしました', en: 'Uploaded "{name}" to Drive' },
  'local.uploadedN': { ja: '{n} 件を Drive にアップロードしました', en: 'Uploaded {n} item(s) to Drive' },
  'local.uploadFailed': { ja: 'アップロード失敗: {msg}', en: 'Upload failed: {msg}' },
  'local.attachTitle': { ja: '「{name}」を最初にどちらで表示しますか？', en: 'Show "{name}" as which to start?' },
  'local.asMain': { ja: 'メイン字幕にする', en: 'As main subtitle' },
  'local.asSub': { ja: 'サブ字幕にする', en: 'As sub subtitle' },
  'local.attachNote': { ja: '再生中の字幕設定で後から変えられます。', en: 'You can change this later in the player\'s subtitle settings.' },
  'local.cancel': { ja: 'キャンセル', en: 'Cancel' },
  'local.attached': { ja: '字幕を適用しました（{n} 行）', en: 'Subtitle applied ({n} lines)' },
  'local.subParseFail': { ja: '字幕を読み取れませんでした（SRT/VTT 対応）', en: 'Could not read subtitle (SRT/VTT only)' },
  'cloud.lockedPlay': { ja: 'このプランでは利用できません', en: 'Not available on your plan' },

  // --- インストール案内バナー ---
  'install.benefit': {
    ja: 'ホーム画面に追加すると、オフラインで使え保存も消えにくくなります。',
    en: 'Add to your home screen to use it offline and keep your data from being cleared.',
  },
  'install.button': { ja: 'インストール', en: 'Install' },
  'install.close': { ja: '閉じる', en: 'Close' },
  'install.iosHint': {
    ja: '共有ボタン {icon} →「ホーム画面に追加」',
    en: 'Share button {icon} → "Add to Home Screen"',
  },

  // --- 共通 ---
  'common.remove': { ja: '削除', en: 'Remove' },
  'common.close': { ja: '閉じる', en: 'Close' },
  'common.ok': { ja: 'OK', en: 'OK' },
  'common.speak': { ja: '発音', en: 'Pronounce' },

  // --- 相対時間（SRS の次回予定など） ---
  'time.min': { ja: '{n}分', en: '{n}m' },
  'time.hour': { ja: '{n}時間', en: '{n}h' },
  'time.day': { ja: '{n}日', en: '{n}d' },
  'time.month': { ja: '{n}か月', en: '{n}mo' },

  // --- ライブラリ ---
  'library.title': { ja: 'ライブラリ', en: 'Library' },
  'library.refresh': { ja: '更新', en: 'Refresh' },
  'library.import': { ja: '取り込み', en: 'Import' },
  'library.searchPlaceholder': { ja: 'ライブラリ内を検索', en: 'Search library' },
  'library.emptyAll': {
    ja: '素材がありません。「取り込み」で追加、または設定で Google Drive と連携してください。',
    en: 'No materials. Tap Import, or connect Google Drive in Settings.',
  },
  'library.noMatch': { ja: '一致する素材がありません。', en: 'No matching materials.' },
  'library.removeTitle': { ja: '「{name}」を削除', en: 'Delete "{name}"' },
  'library.removeLocal': { ja: 'この端末から削除', en: 'Remove from this device' },
  'library.removeLocalDesc': { ja: '原本は Drive に残ります（容量の解放）。', en: 'The original stays on Drive (frees space).' },
  'library.removeDrive': { ja: 'Drive からも削除（全端末）', en: 'Delete from Drive (all devices)' },
  'library.removeDriveDesc': { ja: '取り消せません。連携した全端末から消えます。', en: 'Cannot be undone. Removed from all linked devices.' },
  'library.removingDrive': { ja: 'Drive から削除中…', en: 'Deleting from Drive…' },
  'library.removeDriveFailed': { ja: 'Drive 削除に失敗: {msg}', en: 'Drive delete failed: {msg}' },
  'library.removeDriveConfirm': { ja: '「{name}」を Drive から削除します。取り消せません（連携した全端末から消えます）。', en: 'Delete "{name}" from Drive? Cannot be undone (removed from all linked devices).' },
  'library.subtitleBadge': { ja: '字幕', en: 'Subtitles' },
  'library.download': { ja: 'DL', en: 'Get' },
  'library.notUploaded': { ja: '未アップ', en: 'Not uploaded' },
  'library.untitledFolder': { ja: '(無題フォルダ)', en: '(Untitled folder)' },
  'library.untitled': { ja: '(無題)', en: '(Untitled)' },
  'library.itemCount': { ja: '{n} 件', en: '{n} items' },
  'library.empty': { ja: 'このフォルダは空です。', en: 'This folder is empty.' },
  'library.setupHint': {
    ja: '設定タブで Google Drive と連携すると一覧が出ます。',
    en: 'Connect Google Drive in the Settings tab to see your library.',
  },
  'library.notConnected': {
    ja: '未連携：設定→Google Drive で連携するとライブラリを閲覧できます（端末内 {n} 件）。',
    en: 'Not connected: connect Google Drive in Settings to browse your library ({n} on device).',
  },
  'library.loading': { ja: '一覧を取得中…', en: 'Loading…' },
  'library.countStatus': { ja: '{n} 件（DL済み {d} 件）', en: '{n} items ({d} downloaded)' },
  'library.fetchFailed': { ja: '取得に失敗', en: 'Failed to load' },
  'library.offlineCount': { ja: 'オフライン表示: 取り込み済み {n} 件', en: 'Offline: {n} downloaded items' },
  'library.needOnline': {
    ja: 'ダウンロードにはオンライン接続が必要です。',
    en: 'Downloading requires an internet connection.',
  },
  'library.fetchingSubs': { ja: '字幕取得中…', en: 'Fetching subtitles…' },
  'library.dlFailed': { ja: 'DL失敗: {msg}', en: 'Download failed: {msg}' },

  // --- 同期（更新ボタン共通） ---
  'sync.refresh': { ja: '更新', en: 'Refresh' },
  'sync.title': { ja: '同期', en: 'Sync' },
  'sync.cardDesc': {
    ja: '連携すると、取り込んだ素材は自分の Google Drive に同期され、どの端末でも使えます。',
    en: 'When connected, imported materials sync to your Google Drive and are usable on any device.',
  },
  'sync.autoUpload': { ja: 'メディアの自動アップロード', en: 'Auto-upload media' },
  'sync.autoUpload.wifi': { ja: 'Wi-Fi のときだけ', en: 'Wi-Fi only' },
  'sync.autoUpload.always': { ja: '常に（モバイル回線でも）', en: 'Always (incl. cellular)' },
  'sync.download': { ja: 'メディアのダウンロード', en: 'Media download' },
  'sync.download.wifi': { ja: 'Wi-Fi のときだけ（全ダウンロード）', en: 'Wi-Fi only (full download)' },
  'sync.download.stream': { ja: 'ストリーミングも可（モバイルでも視聴）', en: 'Allow streaming (incl. cellular)' },
  'sync.connected': { ja: 'Drive 連携済み', en: 'Drive connected' },
  'sync.notConnectedBadge': { ja: '未連携', en: 'Not connected' },
  'sync.connect': { ja: '連携する', en: 'Connect' },
  'sync.syncing': { ja: '同期中…', en: 'Syncing…' },
  'sync.got': { ja: '{n} 件取得', en: 'Got {n}' },
  'sync.upToDate': { ja: '最新です', en: 'Up to date' },

  // --- 単語ページ ---
  'words.title': { ja: '単語', en: 'Words' },
  'words.searchPlaceholder': { ja: '単語・意味で検索', en: 'Search words or meanings' },
  'words.count': { ja: '{n} 語', en: '{n} words' },
  'words.statusNew': { ja: '新規', en: 'New' },
  'words.statusDue': { ja: '復習可', en: 'Due' },
  'words.next': { ja: '次回 {t}', en: 'Next {t}' },
  'words.noMatch': { ja: '一致する単語がありません。', en: 'No matching words.' },
  'words.empty': {
    ja: '保存した単語がここに表示されます。再生中に字幕の単語を辞書から保存できます。',
    en: 'Saved words appear here. While playing, save words from the subtitle dictionary.',
  },

  // --- 復習（SRS） ---
  'review.title': { ja: '復習', en: 'Review' },
  'review.noneNow': { ja: 'いまは復習する単語がありません。', en: 'No words to review right now.' },
  'review.nextIn': { ja: '次の復習まで約 {t}。', en: 'Next review in about {t}.' },
  'review.saveToReview': {
    ja: '単語を保存すると、ここで復習できます。',
    en: 'Save some words and you can review them here.',
  },
  'review.done': { ja: 'お疲れさま！ {n} 枚を復習しました。', en: 'Nice work! You reviewed {n} cards.' },
  'review.again': { ja: 'もう一度確認', en: 'Review again' },
  'review.reveal': { ja: '意味を見る', en: 'Show meaning' },
  'review.progress': { ja: '{i} / {n}', en: '{i} / {n}' },
  'review.grade.again': { ja: 'もう一度', en: 'Again' },
  'review.grade.hard': { ja: '難しい', en: 'Hard' },
  'review.grade.good': { ja: '普通', en: 'Good' },
  'review.grade.easy': { ja: '簡単', en: 'Easy' },

  // --- プレーヤ ---
  'player.mediaMissing': {
    ja: 'メディアが端末にありません（再ダウンロードしてください）。',
    en: "Media isn't on this device (please download it again).",
  },
  'player.notFound': { ja: '項目が見つかりません。', en: 'Item not found.' },
  'player.dict.notFound': { ja: '辞書に見つかりません', en: 'Not found in dictionary' },
  'player.dict.notLoaded': {
    ja: '辞書が未取込です（設定→辞書をダウンロード）',
    en: 'Dictionary not imported (Settings → Download dictionary)',
  },
  'player.dict.phrase': { ja: '熟語', en: 'Phrase' },
  'player.dict.save': { ja: '単語を保存', en: 'Save word' },
  'player.dict.saved': { ja: '保存しました', en: 'Saved' },
  'player.dict.alreadySaved': { ja: '保存済み', en: 'Already saved' },
  'player.toast.saved': { ja: '「{w}」を保存しました', en: 'Saved "{w}"' },
  'player.toast.dup': { ja: '「{w}」は保存済み', en: '"{w}" already saved' },
  'player.loopAll': { ja: '全体リピート', en: 'Repeat all' },
  'player.fullscreen': { ja: '全画面', en: 'Fullscreen' },
  'player.fullscreenExit': { ja: '全画面を終了', en: 'Exit fullscreen' },
  'player.subSettings': { ja: '字幕設定', en: 'Subtitle settings' },
  'player.prevSeg': { ja: '前の文', en: 'Previous line' },
  'player.replaySeg': { ja: 'この文を頭から', en: 'Replay this line' },
  'player.nextSeg': { ja: '次の文', en: 'Next line' },
  'player.loopSeg': { ja: '文ループ', en: 'Loop line' },
  'player.speed': { ja: '再生速度', en: 'Playback speed' },
  'player.ab': { ja: 'A-Bリピート', en: 'A-B repeat' },
  'player.sheet.main': { ja: 'メイン字幕', en: 'Main subtitles' },
  'player.sheet.sub': { ja: 'サブ字幕', en: 'Secondary subtitles' },
  'player.sheet.none': { ja: 'なし', en: 'None' },
  'player.sheet.pos': { ja: '字幕の位置', en: 'Subtitle position' },
  'player.sheet.timing': { ja: 'タイミング', en: 'Timing' },
  'player.locked': {
    ja: '無料枠（3本）を超えています。購入すると再生できます（素材は端末に残っています）。',
    en: 'Beyond the free 3 items. Purchase to play (it stays on your device).',
  },
  'player.unlock': { ja: '購入で解放', en: 'Unlock by purchasing' },
  'player.toc': { ja: '目次', en: 'Chapters' },
  'player.tocItem': { ja: 'チャプター {n}', en: 'Chapter {n}' },
  'player.mode.stop': { ja: '再生したら停止', en: 'Stop after track' },
  'player.mode.order': { ja: '順番に再生', en: 'Play in order' },
  'player.mode.shuffle': { ja: 'シャッフル再生', en: 'Shuffle' },
  'player.mode.repeat': { ja: 'リピート再生', en: 'Repeat track' },

  // --- ディクテーション（書き取りモード） ---
  'dct.enter': { ja: 'ディクテーション', en: 'Dictation' },
  'dct.title': { ja: 'ディクテーション', en: 'Dictation' },
  'dct.exit': { ja: '閉じる', en: 'Close' },
  'dct.playpause': { ja: '再生 / 一時停止', en: 'Play / Pause' },
  'dct.replay': { ja: 'この文を聞く', en: 'Play this line' },
  'dct.loop': { ja: '区間ループ', en: 'Loop segment' },
  'dct.prev': { ja: '前の文', en: 'Previous line' },
  'dct.next': { ja: '次の文', en: 'Next line' },
  'dct.inputPlaceholder': { ja: '聞こえたとおりに入力…', en: 'Type what you hear…' },
  'dct.grade': { ja: '採点', en: 'Check' },
  'dct.reveal': { ja: '正解を見る', en: 'Show answer' },
  'dct.list': { ja: '文の一覧', en: 'Sentences' },
  'dct.overall': { ja: '{done}/{total} 文 練習済み', en: '{done}/{total} sentences done' },
  'dct.overallAvg': { ja: ' ・ 平均{avg}%', en: ' · avg {avg}%' },
  'dct.filterAll': { ja: 'すべて', en: 'All' },
  'dct.filterTodo': { ja: '未挑戦', en: 'To do' },
  'dct.filterWeak': { ja: '苦手', en: 'Weak' },
  'dct.emptyTodo': { ja: '未挑戦の文はありません', en: 'No unattempted sentences' },
  'dct.emptyNone': { ja: '該当する文はありません', en: 'No matching sentences' },
  'dct.badgeTodo': { ja: '未', en: 'new' },
  'dct.progress': { ja: '文 {n} / {total}', en: 'Sentence {n} / {total}' },
  'dct.progressHist': { ja: ' ・ 最高{best}%（{attempts}回）', en: ' · best {best}% ({attempts}×)' },
  'dct.score': { ja: '正答率 {acc}%（{lcs}/{total} 語）', en: 'Accuracy {acc}% ({lcs}/{total} words)' },
  'dct.answerLabel': { ja: '正解（緑＝当てた / 赤＝外した・抜け）', en: 'Answer (green = got it / red = missed)' },
  'dct.answerPlain': { ja: '正解', en: 'Answer' },
  'dct.revealed': { ja: '正解を表示', en: 'Answer shown' },
  'dct.noSubs': { ja: 'この素材には字幕（テキスト）がないので書き取りできません。', en: 'This material has no subtitle text, so dictation is unavailable.' },
  'dct.loading': { ja: '読み込み中…', en: 'Loading…' },

  // --- 設定 ---
  'settings.title': { ja: '設定', en: 'Settings' },
  'settings.cloud.title': { ja: 'クラウド (Cloudflare R2)', en: 'Cloud (Cloudflare R2)' },
  'settings.cloud.desc': {
    ja: 'PC 版と同じ R2 の接続情報を入力します。値はこの端末内にのみ保存されます。',
    en: 'Enter the same R2 connection details as the desktop app. Values are stored only on this device.',
  },
  'settings.field.endpoint': { ja: 'エンドポイント', en: 'Endpoint' },
  'settings.field.region': { ja: 'リージョン', en: 'Region' },
  'settings.field.bucket': { ja: 'バケット名', en: 'Bucket name' },
  'settings.field.keyId': { ja: 'キーID', en: 'Key ID' },
  'settings.field.appKey': { ja: 'アプリケーションキー', en: 'Application key' },
  'settings.test': { ja: '接続テスト', en: 'Test connection' },
  'settings.save': { ja: '保存', en: 'Save' },
  'settings.dict.title': { ja: '辞書（タップ辞書用）', en: 'Dictionary (for tap lookup)' },
  'settings.dict.desc2': {
    ja: 'タップ辞書に使う辞書。複数入れて ON/OFF を切り替えられます（有効な辞書をまとめて表示）。',
    en: 'Dictionaries for tap lookup. Add several and toggle them on/off (enabled ones are shown together).',
  },
  'settings.dict.none': { ja: '辞書がありません。下から追加してください。', en: 'No dictionaries yet. Add one below.' },
  'settings.dict.words': { ja: '{n} 語', en: '{n} words' },
  'settings.dict.cloud': { ja: 'クラウドから追加', en: 'Add from cloud' },
  'settings.dict.recommended': { ja: '（おすすめ）', en: '(recommended)' },
  'settings.dict.import': { ja: 'ファイルから取り込む', en: 'Import file' },
  'settings.dict.loadingCatalog': { ja: '配布辞書を取得中…', en: 'Loading available dictionaries…' },
  'settings.dict.catalogEmpty': {
    ja: 'クラウドに配布辞書が見つかりません（dict/catalog.json）。',
    en: 'No distributable dictionaries found in the cloud (dict/catalog.json).',
  },
  'settings.dict.added': { ja: '「{name}」を追加しました（{n} 語）', en: 'Added "{name}" ({n} words)' },
  'settings.dict.deleteConfirm': { ja: '辞書「{name}」を削除しますか？', en: 'Delete dictionary "{name}"?' },
  'settings.playback': { ja: '再生', en: 'Playback' },
  'settings.subStyle.title': { ja: '字幕の見た目', en: 'Subtitle appearance' },
  'settings.subStyle.size': { ja: '文字サイズ', en: 'Text size' },
  'settings.subStyle.weight': { ja: '太さ', en: 'Weight' },
  'settings.subStyle.weightNormal': { ja: '標準', en: 'Normal' },
  'settings.subStyle.weightBold': { ja: '太字', en: 'Bold' },
  'settings.subStyle.color': { ja: '文字色', en: 'Color' },
  'settings.subStyle.colorYellow': { ja: '黄', en: 'Yellow' },
  'settings.subStyle.colorWhite': { ja: '白', en: 'White' },
  'settings.subStyle.colorCyan': { ja: '水色', en: 'Cyan' },
  'settings.subStyle.colorGreen': { ja: '緑', en: 'Green' },
  'settings.subStyle.outline': { ja: '縁取り', en: 'Outline' },
  'settings.subStyle.outlineOn': { ja: 'あり（読みやすい）', en: 'On (readable)' },
  'settings.subStyle.outlineOff': { ja: 'なし', en: 'Off' },
  'settings.skipLabel': { ja: '{icons} の移動秒数', en: '{icons} skip seconds' },
  'settings.storage': { ja: 'ストレージ', en: 'Storage' },
  'settings.usage': { ja: '使用量: {usage} / 上限の目安: {quota}', en: 'Used: {usage} / Approx. limit: {quota}' },
  'settings.persist.label': { ja: '永続化', en: 'Persistent storage' },
  'settings.persist.enabled': { ja: '有効', en: 'On' },
  'settings.persist.disabled': { ja: '無効', en: 'Off' },
  'settings.persist.onNote': { ja: '（OSの容量逼迫時を除き消えにくい）', en: " (won't be cleared except under severe storage pressure)" },
  'settings.persist.offNote': {
    ja: 'ホーム画面に追加すると有効化しやすくなります。',
    en: 'Adding to your home screen makes this easier to enable.',
  },
  'settings.persist.enable': { ja: '消えにくくする（永続化）', en: 'Make storage persistent' },
  'settings.lang.title': { ja: '言語', en: 'Language' },
  'settings.lang.label': { ja: '表示言語', en: 'Display language' },
  'settings.status.fillAll': { ja: 'すべての項目を入力してください。', en: 'Please fill in all fields.' },
  'settings.status.saved': { ja: '保存しました。', en: 'Saved.' },
  'settings.status.connecting': { ja: '接続中…', en: 'Connecting…' },
  'settings.status.connectOk': { ja: '接続OK（オブジェクト {n} 件を確認）', en: 'Connected ({n} objects found).' },

  // --- ペイウォール（ロックUX：未解放機能をタップしたとき） ---
  'paywall.unlock': { ja: '{plan} で解放', en: 'Unlock with {plan}' },
  'paywall.cta': { ja: 'プランを見る', en: 'See plans' },
  'paywall.title': { ja: '無料体験はここまで', en: "That's the free trial" },
  'paywall.desc': {
    ja: '無料で使えるのは {n} 本までです。購入するとすべての素材を無制限に使えます（全機能込み・買い切り）。',
    en: 'The free trial is limited to {n} items. Purchase to use unlimited materials (all features, one-time buy).',
  },
  'paywall.plan.month': { ja: '月プラン', en: 'Monthly' },
  'paywall.plan.year': { ja: '年プラン', en: 'Yearly' },
  'paywall.plan.lifetime': { ja: '買い切り', en: 'Lifetime' },
  'paywall.price.month': { ja: '¥500 / 月', en: '¥500/mo' },
  'paywall.price.year': { ja: '¥3,000 / 年', en: '¥3,000/yr' },
  'paywall.price.lifetime': { ja: '¥10,000', en: '¥10,000' },
  'paywall.note.year': { ja: '月払いより50%お得', en: 'Save 50% vs monthly' },
  'paywall.note.lifetime': { ja: '一度だけ・ずっと使える', en: 'One-time · forever' },
  'paywall.recommended': { ja: 'おすすめ', en: 'Best value' },
  'paywall.feat.all': { ja: 'すべての機能が使える', en: 'All features included' },
  'paywall.feat.unlimited': { ja: '素材を無制限に取り込み・ダウンロード', en: 'Unlimited imports & downloads' },
  'paywall.feat.lifetime': { ja: '最初の3本は無料でお試し', en: 'First 3 items free to try' },
  'paywall.restore': { ja: '購入を復元', en: 'Restore purchase' },
  'paywall.restoreNone': { ja: '（プロトタイプ：復元は未実装）', en: '(Prototype: restore not implemented)' },
  'paywall.buyDummy': { ja: '購入する（ダミー）', en: 'Purchase (dummy)' },
  'free.quota': { ja: '無料枠 {used}/{limit} 本', en: 'Free {used}/{limit}' },
  'paywall.note': {
    ja: '（プロトタイプ：実際の課金は未実装。「購入（ダミー）」で解放、設定→開発者で戻せます）',
    en: '(Prototype: billing is not implemented. "Purchase (dummy)" unlocks it; revert in Settings → Developer.)',
  },
  'paywall.cap.browse': {
    ja: 'クラウドのライブラリを見るにはログインが必要です。',
    en: 'Sign in to browse your cloud library.',
  },
  'paywall.cap.vocab': {
    ja: '保存した単語・復習(SRS)・文脈の音声を全端末で同期・バックアップできます。',
    en: 'Sync and back up your saved words, reviews (SRS), and context clips across all your devices.',
  },
  'paywall.cap.audio': {
    ja: '音声をダウンロードして再生・全端末で同期できます。',
    en: 'Download audio to play it and sync it across all your devices.',
  },
  'paywall.cap.video': { ja: '動画を全端末で同期できます。', en: 'Sync video across all your devices.' },
  'paywall.capGeneric': { ja: 'この機能は有料プランで解放されます。', en: 'This feature is available on a paid plan.' },
  // プラン名（ロックUXの「○○で解放」表示用・ユーザー向け短縮名）
  'plan.free-in': { ja: 'ログイン', en: 'Sign-in' },
  'plan.t2': { ja: 'T2（単語同期）', en: 'T2 (Vocabulary sync)' },
  'plan.t3': { ja: 'T3（音声）', en: 'T3 (Audio)' },
  'plan.t4': { ja: 'T4（動画）', en: 'T4 (Video)' },

  // --- エラー（lib から throw されるメッセージ） ---
  'err.r2NotSet': { ja: 'R2 が未設定です', en: 'R2 is not configured' },
  'err.r2NotSetLib': {
    ja: 'R2 が未設定です（設定タブで入力してください）',
    en: 'R2 is not configured (enter your details in the Settings tab)',
  },
  'err.r2Unreachable': {
    ja:
      'R2 に到達できませんでした。\n' +
      '・バケットの CORS でこのアプリのオリジンの GET/PUT/HEAD を許可しているか\n' +
      '・エンドポイント/リージョンが正しいか を確認してください。',
    en:
      "Couldn't reach R2.\n" +
      '・Check that the bucket CORS allows GET/PUT/HEAD from this app\'s origin\n' +
      '・Check that the endpoint/region are correct.',
  },
  'err.r2BadKey': {
    ja: 'キーID / アプリケーションキーが正しくありません。',
    en: 'Key ID or application key is incorrect.',
  },
  'err.r2BadBucket': {
    ja: 'バケット名が違うか、このキーにアクセス権がありません。',
    en: 'Bucket name is wrong, or this key lacks access.',
  },
  'err.r2Generic': { ja: '接続に失敗しました: {detail}', en: 'Connection failed: {detail}' },
  'err.storageFull': {
    ja: '端末の空き容量が不足しています。不要な項目を削除してから再試行してください。',
    en: 'Your device is low on storage. Delete unneeded items and try again.',
  },
}

// 文言を引く。未知キーはキー名をそのまま返す（落とさない）。{name} は params で置換。
export function t(key, params) {
  const entry = STR[key]
  let s = entry ? entry[lang] ?? entry.ja ?? key : key
  if (params) {
    for (const k of Object.keys(params)) s = s.replaceAll(`{${k}}`, String(params[k]))
  }
  return s
}

// 起動時に <html lang> を実際の言語へ合わせる（アクセシビリティ/フォント選択のため）。
try {
  document.documentElement.lang = lang
} catch {
  /* SSR 等 document 不在でも続行 */
}
